import { truncateToTwoDecimals } from "@/_old/utils/number";
import { CircularProgress } from "@/core/components/ui/circular-progress";
import { useCancellationRate } from "../hooks/use-cancellation-rate";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPercent, useIntlLocale } from "@/core/utils/locale";
import { Card } from "@woba/ds-web-next";
import { DashboardEmptyState } from "../../dashboard-empty-state";

interface ReservationCancellationsCardProps {
  startDate: string;
  endDate: string;
  companyId: number;
  cities?: string[] | null;
  spaces?: number[] | null;
  users?: number[] | null;
  status?: number[] | null;
  types?: string[] | null;
  group_id?: number | null;
}

export function ReservationCancellationsCard({
  startDate,
  endDate,
  companyId,
  cities,
  spaces,
  users,
  status,
  types,
  group_id,
}: ReservationCancellationsCardProps) {
  const [t] = useTranslation(["dashboard"]);
  const locale = useIntlLocale();

  const { data, isLoading } = useCancellationRate({
    startDate,
    endDate,
    companyId,
    cities,
    spaces,
    users,
    status,
    types,
    group_id,
  });

  const verifyEmptyData = useMemo(() => {
    return !isLoading && (!data || data?.cancellationRate === 0);
  }, [data, isLoading]);

  const cancellationStatus = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    label: string;
    color: string;
  } => {
    if (!data?.cancellationRate || data?.cancellationRate === 0) {
      return {
        type: "good",
        label: t("dashboard-cancellation-healthy-index", "Índice saudável"),
        color: "text-teal-600",
      };
    }

    if (data.cancellationRate <= 5) {
      return {
        type: "good",
        label: t("dashboard-cancellation-healthy-index", "Índice saudável"),
        color: "text-teal-600",
      };
    }

    if (data.cancellationRate <= 10) {
      return {
        type: "warning",
        label: t("dashboard-cancellation-moderate-index", "Índice moderado"),
        color: "text-amber-600",
      };
    }

    return {
      type: "danger",
      label: t("dashboard-cancellation-high-index", "Índice alto"),
      color: "text-red-600",
    };
  }, [data, t]);

  // Card do Workbase DS (heading + descrição + bottomContent) — Figma
  // 6037:4578. O anel segue o CircularProgress do projeto: o Workbase não tem
  // componente de gauge (Chart/Ring).
  const cardProps = {
    heading: t(
      "dashboard-reservation-cancellations",
      "Cancelamentos de reservas",
    ) as string,
    description: t(
      "dashboard-reservation-cancellations-description",
      "Percentual sobre o total de reservas",
    ) as string,
    className: "h-full",
  };

  if (isLoading) {
    return (
      <Card
        {...cardProps}
        bottomContent={
          <div className="flex flex-col gap-6 justify-center items-center">
            <div className="w-36 h-36 bg-gray-200 rounded-full animate-pulse" />
            <div className="mx-auto w-16 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        }
      />
    );
  }

  if (verifyEmptyData) {
    return (
      <Card
        {...cardProps}
        bottomContent={
          <div className="flex items-center justify-center h-[200px]">
            <DashboardEmptyState />
          </div>
        }
      />
    );
  }

  const rateValue = data?.cancellationRate
    ? truncateToTwoDecimals(data.cancellationRate)
    : 0;

  return (
    <Card
      {...cardProps}
      bottomContent={
        <div className="flex flex-col gap-4 justify-center items-center">
          <CircularProgress
            value={rateValue}
            valueLabel={formatPercent(rateValue, locale)}
            color={cancellationStatus.color}
            label="Reservas"
          />
          <p className={`text-sm ${cancellationStatus.color}`}>
            {cancellationStatus.label}
          </p>
        </div>
      }
    />
  );
}
